import { Err, Ok, Result } from 'ts-res';
import { CustomDataType, KameleoonData } from 'src/kameleoonData/types';
import { KameleoonError, KameleoonException } from 'src/kameleoonError';
import { EvaluationDataType, ValueMatchType } from 'src/targeting/types';
import { Parser } from 'src/utilities';
import { ConditionDataType, ICondition } from './types';
import { KameleoonLogger } from 'src/logging/kameleoonLogger';

export class CustomData implements ICondition {
  private customDataIndex: number;
  private matchType?: ValueMatchType | null;
  private conditionValue?: string | null;
  private error?: KameleoonError;
  private range?: number[] | null;

  constructor({
    customDataIndex,
    valueMatchType,
    value,
    range,
  }: ConditionDataType) {
    this.customDataIndex = customDataIndex ? Number(customDataIndex) : -1;
    this.matchType = valueMatchType;
    this.conditionValue = value;
    this.range = range;
  }

  public evaluate({
    targetingData,
  }: EvaluationDataType): Result<boolean, KameleoonError> {
    const customData =
      targetingData?.[KameleoonData.CustomData]?.[this.customDataIndex];

    if (this.matchType === ValueMatchType.UNDEFINED) {
      return Ok(!customData);
    }

    if (customData) {
      const { value: customDataValue } = customData;
      const result = this.checkCondition(customDataValue);

      if (this.error) {
        return Err(this.error);
      }

      return Ok(result);
    }

    return Ok(false);
  }

  private checkCondition(
    customDataValue: CustomDataType['value'] | string,
  ): boolean {
    if (
      typeof this.conditionValue !== 'string' &&
      this.matchType !== ValueMatchType.TRUE &&
      this.matchType !== ValueMatchType.FALSE &&
      this.matchType !== ValueMatchType.RANGE
    ) {
      KameleoonLogger.error`Unexpected condition value for CustomData condition: ${this.conditionValue}`;
      return false;
    }

    // --- Note ---
    // `TRUE`/`FALSE`/`RANGE` do not use condition value
    // so we can safely assume that `this.conditionValue` is a string at this point
    // for other match types
    const conditionValue = this.conditionValue as string;

    // --- Note ---
    // `AMONG_VALUES` and `RANGE` are checked separately to avoid recursion overhead
    // for `JSON.parse` inside `this.parseArray`
    if (this.matchType === ValueMatchType.AMONG_VALUES) {
      const result = this.parseArray(conditionValue);

      if (!result.ok) {
        this.error = result.error;

        return false;
      }

      const resultData = new Set(result.data);

      return Array.isArray(customDataValue)
        ? customDataValue.some((value) => resultData.has(value))
        : result.data.includes(customDataValue);
    }

    if (this.matchType === ValueMatchType.RANGE) {
      const result = this.parseRange(this.range);

      if (!result.ok) {
        this.error = result.error;

        return false;
      }

      const [lowerBound, upperBound] = result.data;

      const isInRange = (value: number) =>
        value >= lowerBound && value <= upperBound;

      return Array.isArray(customDataValue)
        ? customDataValue.some((value) => isInRange(Number(value)))
        : isInRange(Number(customDataValue));
    }

    if (Array.isArray(customDataValue)) {
      return customDataValue.some((value) => this.checkCondition(value));
    }

    switch (this.matchType) {
      case ValueMatchType.CONTAINS:
        return customDataValue.includes(conditionValue);
      case ValueMatchType.EXACT:
        return customDataValue === this.conditionValue;
      case ValueMatchType.REGULAR_EXPRESSION:
        return Parser.parseRegExp(conditionValue).test(customDataValue);
      case ValueMatchType.LOWER:
        return Number(customDataValue) < Number(this.conditionValue);
      case ValueMatchType.EQUAL:
        return Number(customDataValue) === Number(this.conditionValue);
      case ValueMatchType.GREATER:
        return Number(customDataValue) > Number(this.conditionValue);
      case ValueMatchType.TRUE:
        return customDataValue.toLowerCase() === 'true';
      case ValueMatchType.FALSE:
        return customDataValue.toLowerCase() === 'false';
      default:
        KameleoonLogger.error`Unexpected comparing operation for CustomData condition: ${this.matchType}`;
        return false;
    }
  }

  private parseArray(value: string): Result<string[], KameleoonError> {
    try {
      const result = JSON.parse(value);

      if (Array.isArray(result)) {
        return Ok(result);
      }
    } catch (err) {
      KameleoonLogger.error`Failed to parse array: ${value} in CustomData condition`;
      return Err(
        new KameleoonError(KameleoonException.AmongValuesCheck, err, value),
      );
    }

    return Ok([]);
  }

  private parseRange(
    array?: number[] | null,
  ): Result<[number, number], KameleoonError> {
    if (!array || array.length !== 2) {
      KameleoonLogger.error`Failed to parse range: ${array} in CustomData condition`;
      return Err(new KameleoonError(KameleoonException.RangeCheck, array));
    }

    return Ok(array as [number, number]);
  }
}
